import axios from 'axios';
import { CSRF_TOKEN } from './authTypes';

type AuthCSRFTokenResponse = {
  token?: string;
};

type AuthCSRFTokenOptions = {
  forceRefresh?: boolean;
};

let cachedAuthCSRFToken: string | null = null;
let pendingAuthCSRFTokenRequest: Promise<string> | null = null;

// Bumped whenever the session changes. A token request that started under an
// earlier generation was issued against a session that no longer exists, so its
// token must never reach the cache.
let authCSRFTokenGeneration = 0;

const SUPERSEDED_TOKEN_MAX_ATTEMPTS = 3;

const readStoredAuthCSRFToken = (): string | null => {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }

  return sessionStorage.getItem(CSRF_TOKEN);
};

const storeAuthCSRFToken = (token: string) => {
  cachedAuthCSRFToken = token;

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(CSRF_TOKEN, token);
  }
};

const requestAuthCSRFToken = async (attempt: number = 1): Promise<string> => {
  const requestGeneration = authCSRFTokenGeneration;
  const response = await axios.post<AuthCSRFTokenResponse>(`/api/auth/csrftoken`);

  if (response.status != 200) {
    console.error('Unable to get token');
    return '';
  }

  const token = response.data.token ?? '';

  // Signing in or registering while this request was in flight means the token
  // belongs to the previous session. Caching it would poison every later request.
  if (requestGeneration !== authCSRFTokenGeneration) {
    console.debug('Discarding CSRF token issued for a superseded session', { attempt });

    if (attempt >= SUPERSEDED_TOKEN_MAX_ATTEMPTS) {
      return '';
    }

    const currentToken = cachedAuthCSRFToken ?? readStoredAuthCSRFToken();
    if (currentToken) {
      cachedAuthCSRFToken = currentToken;
      return currentToken;
    }

    return requestAuthCSRFToken(attempt + 1);
  }

  storeAuthCSRFToken(token);

  return token;
};

export const clearAuthCSRFTokenCache = () => {
  cachedAuthCSRFToken = null;
  pendingAuthCSRFTokenRequest = null;
  authCSRFTokenGeneration += 1;

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(CSRF_TOKEN);
  }
};

export const getCachedAuthCSRFToken = async ({ forceRefresh = false }: AuthCSRFTokenOptions = {}): Promise<string> => {
  if (pendingAuthCSRFTokenRequest) {
    return pendingAuthCSRFTokenRequest;
  }

  if (!forceRefresh) {
    const currentToken = cachedAuthCSRFToken ?? readStoredAuthCSRFToken();

    if (currentToken) {
      cachedAuthCSRFToken = currentToken;
      return currentToken;
    }
  }

  pendingAuthCSRFTokenRequest = requestAuthCSRFToken().finally(() => {
    pendingAuthCSRFTokenRequest = null;
  });

  return pendingAuthCSRFTokenRequest;
};
